import { cn } from '@/lib/utils';

export type StatusVariant =
  // General
  | 'active'
  | 'inactive'
  | 'pending'
  | 'draft'
  // Inventory
  | 'in_stock'
  | 'low_stock'
  | 'out_of_stock'
  | 'on_order'
  // Shipments
  | 'booked'
  | 'in_transit'
  | 'delivered'
  | 'delayed'
  | 'cancelled'
  // Orders
  | 'submitted'
  | 'approved'
  | 'rejected'
  | 'processing'
  | 'completed'
  // Maintenance / Work Orders
  | 'scheduled'
  | 'in_progress'
  | 'overdue'
  | 'closed'
  | 'on_hold'
  | 'pending_approval'
  | 'paused'
  // Partnership
  | 'terminated'
  | 'suspended'
  // Priority levels
  | 'low'
  | 'medium'
  | 'high'
  | 'urgent'
  | 'emergency'
  | 'critical'
  // Goods Receipt / Quality
  | 'partially_received'
  | 'pending_inspection'
  | 'passed'
  | 'failed'
  | 'conditional'
  // Discrepancy
  | 'open'
  | 'resolved'
  // Equipment status (EquipmentStatus in the schema)
  | 'available'
  | 'in_use'
  | 'maintenance'
  | 'repair'
  | 'retired'
  | 'disposed'
  // Equipment condition (EquipmentCondition; 'critical' is already declared above)
  | 'excellent'
  | 'good'
  | 'fair'
  | 'poor';

type StatusTone = 'success' | 'warning' | 'info' | 'destructive' | 'neutral';

/**
 * Every status maps to one of five tones; the tone supplies the colours. Keeping
 * the mapping separate from the styling means a palette change touches one place.
 */
const statusTones: Record<StatusVariant, StatusTone> = {
  // Success / terminal-good
  active: 'success',
  in_stock: 'success',
  delivered: 'success',
  approved: 'success',
  completed: 'success',
  closed: 'success',
  passed: 'success',
  resolved: 'success',

  // Warning / awaiting action
  pending: 'warning',
  low_stock: 'warning',
  on_order: 'warning',
  processing: 'warning',
  pending_approval: 'warning',
  pending_inspection: 'warning',
  paused: 'warning',
  on_hold: 'warning',
  conditional: 'warning',
  high: 'warning',
  urgent: 'warning',

  // Info / in-flight
  scheduled: 'info',
  in_transit: 'info',
  submitted: 'info',
  in_progress: 'info',
  booked: 'info',
  partially_received: 'info',
  medium: 'info',

  // Destructive / failed
  out_of_stock: 'destructive',
  delayed: 'destructive',
  rejected: 'destructive',
  overdue: 'destructive',
  cancelled: 'destructive',
  terminated: 'destructive',
  failed: 'destructive',
  open: 'destructive',
  emergency: 'destructive',
  critical: 'destructive',

  // Neutral / dormant
  low: 'neutral',
  inactive: 'neutral',
  draft: 'neutral',
  suspended: 'neutral',

  // Equipment. None of the six EquipmentStatus values were mapped, so every
  // equipment badge in the app fell through to the neutral default and the
  // tone carried no information at all.
  available: 'success',
  in_use: 'info',
  maintenance: 'warning',
  repair: 'warning',
  retired: 'neutral',
  disposed: 'neutral',

  // Equipment condition
  excellent: 'success',
  good: 'success',
  fair: 'warning',
  poor: 'destructive',
};

const toneStyles: Record<StatusTone, string> = {
  success: 'bg-status-success-subtle text-status-success-foreground border-status-success-border',
  warning: 'bg-status-warning-subtle text-status-warning-foreground border-status-warning-border',
  info: 'bg-status-info-subtle text-status-info-foreground border-status-info-border',
  destructive:
    'bg-status-destructive-subtle text-status-destructive-foreground border-status-destructive-border',
  neutral: 'bg-status-neutral-subtle text-status-neutral-foreground border-status-neutral-border',
};

const statusLabels: Record<StatusVariant, string> = {
  active: 'Active',
  inactive: 'Inactive',
  pending: 'Pending',
  draft: 'Draft',
  in_stock: 'In Stock',
  low_stock: 'Low Stock',
  out_of_stock: 'Out of Stock',
  on_order: 'On Order',
  booked: 'Booked',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  delayed: 'Delayed',
  cancelled: 'Cancelled',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  processing: 'Processing',
  completed: 'Completed',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  overdue: 'Overdue',
  closed: 'Closed',
  terminated: 'Terminated',
  suspended: 'Suspended',
  // Work Orders
  on_hold: 'On Hold',
  pending_approval: 'Pending Approval',
  paused: 'Paused',
  // Priority levels
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
  emergency: 'Emergency',
  critical: 'Critical',
  // Goods Receipt / Quality
  partially_received: 'Partially Received',
  pending_inspection: 'Pending Inspection',
  passed: 'Passed',
  failed: 'Failed',
  conditional: 'Conditional',
  // Discrepancy
  open: 'Open',
  resolved: 'Resolved',
  // Equipment
  available: 'Available',
  in_use: 'In Use',
  maintenance: 'Maintenance',
  repair: 'Repair',
  retired: 'Retired',
  disposed: 'Disposed',
  excellent: 'Excellent',
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
};

interface StatusBadgeProps {
  status: StatusVariant | string;
  label?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function StatusBadge({ status, label, className, size = 'md' }: StatusBadgeProps) {
  const variant = (status || 'pending').toLowerCase().replace(/ /g, '_') as StatusVariant;
  const style = toneStyles[statusTones[variant] ?? 'neutral'];
  const displayLabel = label || statusLabels[variant] || status;

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-[10px]',
    md: 'px-2.5 py-0.5 text-[11px]',
    lg: 'px-3 py-1 text-xs',
  };

  return (
    <span
      role="status"
      aria-label={`Status: ${displayLabel}`}
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-md border font-semibold uppercase tracking-wide',
        style,
        sizeClasses[size],
        className
      )}
    >
      {displayLabel}
    </span>
  );
}
